import { useCallback } from "react";
import QRCode from "qrcode";
import { unwrap } from "@brad-tipper/app-frontend";

import { api } from "~/api/client";
import { usePendingMfaToken } from "~/api/mfa-pending";
import { useAuth } from "~/hooks/useAuth";

/**
 * Optional two-factor authentication (an authenticator app such as 1Password,
 * Google Authenticator or Authy), handled by the API.
 *
 * - A person turns it on from their account settings (`TwoFactorCard`).
 * - Once on, a password sign-in returns no session until the 6-digit code is
 *   entered; `MfaGate` asks for it (see `useAuth().completeMfaSignIn`).
 */

export type TotpSetup = {
  /** The QR code as SVG markup, ready for `SvgXml`. */
  qrSvg: string;
  /** The same key as text, for typing into an authenticator app by hand. */
  secret: string;
};

/** Starts enrolment; a half-finished earlier attempt is simply replaced. */
export async function startTotpSetup(): Promise<TotpSetup> {
  const enrollment = await unwrap(api.enrollMfa());
  const qrSvg = await QRCode.toString(enrollment.otpauthUri, {
    type: "svg",
    margin: 1,
  });
  return { qrSvg, secret: enrollment.secret };
}

/**
 * Confirms enrolment with a code from the authenticator. Returns the one-time
 * recovery codes, which are shown once.
 */
export async function verifyTotpCode(code: string): Promise<string[]> {
  const result = await unwrap(
    api.confirmMfaEnrollment({ body: { code: code.trim() } }),
  );
  return result.recoveryCodes;
}

/** Turns two-factor off. Needs a session that already passed the code check. */
export async function disableTotp() {
  await unwrap(api.disableMfa());
}

/** Whether two-factor is on for the signed-in person. */
export function useTotpStatus() {
  const { user, refresh, loading } = useAuth();
  const refreshStatus = useCallback(async () => {
    await refresh();
  }, [refresh]);
  return {
    enabled: Boolean(user?.mfaEnabled),
    loading: loading && !user,
    refresh: refreshStatus,
  };
}

type MfaState = "checking" | "required" | "clear";

/** "required" while a password sign-in is waiting for its authenticator code. */
export function useMfaState(): MfaState {
  return usePendingMfaToken() ? "required" : "clear";
}
